import { Check, X } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingState } from "@/components/data/LoadingState";
import { ErrorState } from "@/components/data/ErrorState";
import { EmptyState } from "@/components/data/EmptyState";
import { useSystemHealth } from "@/lib/queries";
import { fmtDuration, fmtNumber } from "@/lib/format";
import { RelTime } from "@/components/data/RelTime";
import { JsonViewer } from "@/components/data/JsonViewer";
import { StatusBadge } from "@/components/data/StatusBadge";

const ENV_LABELS: Record<string, string> = {
  deepinfra: "DeepInfra",
  apns: "APNs",
  mail: "Mail",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  google_oauth: "Google OAuth",
};

export function SystemPage() {
  const q = useSystemHealth();

  if (q.isLoading) return <LoadingState label="Checking system…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return null;

  const { env_flags, row_counts, recent_crons, last_error, last_request } = q.data;

  return (
    <>
      <PageHeader title="System" description="Configuration, table sizes, recent crons, last error and last request." />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Secrets</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(env_flags).length === 0 ? (
              <EmptyState title="No env flags" />
            ) : (
              <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {Object.entries(env_flags).map(([k, v]) => (
                  <li
                    key={k}
                    className="flex items-center justify-between gap-2 rounded-md border border-border/40 bg-background/30 px-3 py-2 text-sm"
                  >
                    <span className="font-mono text-xs text-muted-foreground">{ENV_LABELS[k] ?? k}</span>
                    {v ? (
                      <Badge variant="success" className="gap-1"><Check className="h-3 w-3" /> set</Badge>
                    ) : (
                      <Badge variant="error" className="gap-1"><X className="h-3 w-3" /> missing</Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Table sizes</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(row_counts).length === 0 ? (
              <EmptyState title="No table info" />
            ) : (
              <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {Object.entries(row_counts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([table, count]) => (
                    <li key={table} className="flex items-center justify-between gap-2 px-1 py-0.5 text-xs">
                      <span className="font-mono text-muted-foreground">{table}</span>
                      <span className="tabular-nums">{fmtNumber(count, { compact: true })}</span>
                    </li>
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent crons</CardTitle>
            <p className="text-xs text-muted-foreground">Latest run of each task in the last 7 days</p>
          </CardHeader>
          <CardContent>
            {recent_crons.length === 0 ? (
              <EmptyState title="No cron runs yet" />
            ) : (
              <ul className="space-y-1.5">
                {recent_crons.map((c) => {
                  const ms = c.duration_ms;
                  const okBadge = c.status === "success" || c.status === "ok";
                  return (
                    <li key={c.id} className="rounded-md border border-border/40 bg-background/30 px-3 py-2 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono">{c.task}</span>
                        <Badge variant={okBadge ? "success" : c.status === "running" ? "info" : "error"}>
                          {c.status}
                        </Badge>
                      </div>
                      <div className="mt-1 flex justify-between text-muted-foreground">
                        <span>finished <RelTime value={c.ran_at} /></span>
                        <span>{ms != null ? fmtDuration(ms) : "—"}</span>
                      </div>
                      {c.error && <div className="mt-1 line-clamp-2 font-mono text-rose-300">{c.error}</div>}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Last error</CardTitle>
          </CardHeader>
          <CardContent>
            {!last_error ? (
              <EmptyState title="No errors logged" />
            ) : (
              <div className="space-y-2 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={last_error.status} />
                  <span className="font-mono">{last_error.method} {last_error.path}</span>
                  <span className="ml-auto text-muted-foreground"><RelTime value={last_error.created_at} /></span>
                </div>
                <div className="rounded-md border border-rose-500/20 bg-rose-500/5 p-3 font-mono text-rose-200/90">
                  {last_error.message}
                </div>
                {last_error.stack && (
                  <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-border/40 bg-background/40 p-3 font-mono text-[11px] text-muted-foreground">
{last_error.stack}
                  </pre>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Last request</CardTitle>
          </CardHeader>
          <CardContent>
            {!last_request ? (
              <EmptyState title="No requests logged" />
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <StatusBadge status={last_request.status} />
                  <span className="font-mono">{last_request.method} {last_request.path}</span>
                  <span className="ml-auto text-muted-foreground"><RelTime value={last_request.created_at} /></span>
                </div>
                <JsonViewer
                  value={{
                    duration_ms: last_request.duration_ms,
                    user_id: last_request.user_id,
                    country: last_request.country,
                    ip: last_request.ip,
                    user_agent: last_request.user_agent,
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
